import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Camera, X, Crop } from 'lucide-react';
import ReactSelect from 'react-select';
import ImageCropperModal from 'components/_UI/ImageCropperModal';
import menuApi from 'api/requests/menu';
import imageUploadApi from 'api/requests/imageUpload';
import orderStationsApi from 'api/requests/orderStations';
import { getInventoryItems } from 'api/requests/inventory';
import colors from 'theme/colors';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

export default function ProductForm({ slug, categories, initialData, onSaved, onCancel, forcedType, forcedPosEventId }) {
    const [name, setName] = useState(initialData?.name || '');
    const [description, setDescription] = useState(initialData?.description || '');
    const [basePrice, setBasePrice] = useState(initialData?.base_price || '');
    const [categoryId, setCategoryId] = useState(initialData?.menu_category_id || '');
    const [sortOrder, setSortOrder] = useState(initialData?.sort_order ?? '');
    const [orderStationId, setOrderStationId] = useState(initialData?.order_station_id ?? initialData?.order_station?.id ?? '');
    const [isActive, setIsActive] = useState(initialData?.is_active ?? true);
    const [isPublic, setIsPublic] = useState(initialData?.is_public ?? true);
    const [stations, setStations] = useState([]);
    const [allGroups, setAllGroups] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState(
        initialData?.option_groups?.map(g => g.id) || []
    );
    const [allInventoryItems, setAllInventoryItems] = useState([]);
    const [techSheets, setTechSheets] = useState(
        initialData?.tech_sheets?.map(t => ({
            inventory_item_id: t.inventory_item_id,
            quantity_used: Number(t.quantity_used),
        })) || []
    );
    const [selectedInventoryOption, setSelectedInventoryOption] = useState(null);
    const [techQuantity, setTechQuantity] = useState('');
    const [preUploadedImage, setPreUploadedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(initialData?.image?.thumb || null);
    const [imageError, setImageError] = useState(null);
    const [cropSourceFile, setCropSourceFile] = useState(null);
    const [isCropperOpen, setIsCropperOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);

    const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB

    function handleImageChange(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > MAX_IMAGE_SIZE) {
            setImageError('Imagem muito grande. Tamanho máximo: 10MB.');
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }
        setImageError(null);
        setCropSourceFile(file);
        setIsCropperOpen(true);
        // Permite reescolher o mesmo arquivo depois de cancelar o recorte
        if (fileInputRef.current) fileInputRef.current.value = '';
    }

    function handleCropConfirm(croppedFile) {
        setPreUploadedImage(croppedFile);
        setImagePreview(URL.createObjectURL(croppedFile));
        setIsCropperOpen(false);
    }

    function handleCropCancel() {
        setIsCropperOpen(false);
        if (!imagePreview) setCropSourceFile(null);
    }

    function removeImage() {
        setPreUploadedImage(null);
        setImagePreview(null);
        setImageError(null);
        setCropSourceFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    }

    async function uploadImage(file) {
        const formData = new FormData();
        formData.append('image', file);
        formData.append('folder', 'products');
        try {
            const { data } = await imageUploadApi.uploadWithFolder(formData);
            return data;
        } catch (err) {
            if (err?.response?.status === 413) {
                throw new Error('image_too_large');
            }
            throw err;
        }
    }

    useEffect(() => {
        menuApi.getOptionGroups(slug)
            .then(({ data }) => setAllGroups(data.data || []))
            .catch(() => {});
        orderStationsApi.list(slug)
            .then(({ data }) => setStations(data.data || data || []))
            .catch(() => {});
        getInventoryItems(slug)
            .then(items => setAllInventoryItems(items || []))
            .catch(() => {});
    }, []);

    function toggleGroup(id) {
        setSelectedGroups(prev =>
            prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]
        );
    }

    function addTechSheetRow() {
        if (!selectedInventoryOption) return;
        const qty = Number(techQuantity);
        if (!qty || qty <= 0) {
            setError('Informe uma quantidade válida para o insumo.');
            return;
        }
        setTechSheets(prev => [...prev, { inventory_item_id: selectedInventoryOption.value, quantity_used: qty }]);
        setSelectedInventoryOption(null);
        setTechQuantity('');
        setError(null);
    }

    function removeTechSheetRow(itemId) {
        setTechSheets(prev => prev.filter(t => t.inventory_item_id !== itemId));
    }

    const inventoryOptions = allInventoryItems
        .filter(i => !techSheets.some(t => t.inventory_item_id === i.id))
        .map(i => ({ value: i.id, label: `${i.name} (${i.unit_of_measure})` }));

    const estimatedCost = techSheets.reduce((sum, t) => {
        const invItem = allInventoryItems.find(i => i.id === t.inventory_item_id);
        return sum + (invItem ? Number(invItem.average_unit_cost) : 0) * t.quantity_used;
    }, 0);
    const priceNum = Number(basePrice) || 0;
    const marginValue = priceNum - estimatedCost;
    const marginPct = priceNum > 0 ? (marginValue / priceNum) * 100 : null;

    function formatCurrency(value) {
        return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }

    async function handleSubmit(e) {
        e.preventDefault();
        if (!name.trim() || (!forcedType && !categoryId) || !basePrice) {
            setError('Nome e preço são obrigatórios');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            let imageId = null;
            if (preUploadedImage) {
                const imageData = await uploadImage(preUploadedImage);
                imageId = imageData?.id || null;
            }

            const payload = {
                name: name.trim(),
                description: description.trim() || null,
                base_price: parseFloat(basePrice),
                ...(!forcedType && { menu_category_id: Number(categoryId) }),
                ...(imageId && { image_id: imageId }),
                ...(sortOrder !== '' && { sort_order: Number(sortOrder) }),
                order_station_id: orderStationId ? Number(orderStationId) : null,
                is_active: isActive,
                is_public: isPublic,
                ...(forcedType && { type: forcedType }),
                ...(forcedPosEventId && { pos_event_id: forcedPosEventId }),
            };
            let saved;
            if (initialData) {
                const { data } = await menuApi.updateProduct(slug, initialData.id, payload);
                saved = data;
            } else {
                const { data } = await menuApi.createProduct(slug, payload);
                saved = data;
            }
            // Sync option groups only when there are selections (backend rejects empty array)
            if (selectedGroups.length > 0) {
                const { data: synced } = await menuApi.syncOptionGroups(slug, saved.id, selectedGroups);
                saved = synced;
            }

            // Sync tech sheets whenever editing (to allow clearing all items) or when
            // creating with at least one item selected
            if (initialData || techSheets.length > 0) {
                const { data: syncedTech } = await menuApi.syncTechSheets(slug, saved.id, techSheets);
                saved = syncedTech;
            }

            onSaved(saved);
        } catch (err) {
            if (err?.message === 'image_too_large') {
                setImageError('Imagem muito grande. Tamanho máximo: 10MB.');
            } else {
                setError('Erro ao salvar. Tente novamente.');
            }
        } finally {
            setLoading(false);
        }
    }

    return (
        <Form onSubmit={handleSubmit}>
            <Field>
                <Label>Foto do produto</Label>
                <ImageUploadArea onClick={() => fileInputRef.current?.click()} $hasImage={!!imagePreview}>
                    {imagePreview ? (
                        <>
                            <ImagePreview src={imagePreview} alt='Pré-visualização da foto do produto' />
                            {cropSourceFile && (
                                <CropImageBtn
                                    type='button'
                                    aria-label='Ajustar recorte da foto'
                                    onClick={e => { e.stopPropagation(); setIsCropperOpen(true); }}
                                >
                                    <Crop size={14} />
                                </CropImageBtn>
                            )}
                            <RemoveImageBtn
                                type='button'
                                aria-label='Remover foto do produto'
                                onClick={e => { e.stopPropagation(); removeImage(); }}
                            >
                                <X size={14} />
                            </RemoveImageBtn>
                        </>
                    ) : (
                        <UploadPlaceholder>
                            <Camera size={22} color={colors.nightLight} />
                            <UploadHint>Clique para adicionar foto</UploadHint>
                        </UploadPlaceholder>
                    )}
                </ImageUploadArea>
                <HiddenInput
                    ref={fileInputRef}
                    type='file'
                    accept='image/*'
                    onChange={handleImageChange}
                />
                {imageError
                    ? <ImageErrorText>{imageError}</ImageErrorText>
                    : <ImageHint>JPG, PNG ou WebP · Máx. 10MB · Recorte 1:1</ImageHint>
                }

                <ImageCropperModal
                    isOpen={isCropperOpen}
                    file={cropSourceFile}
                    aspect={1}
                    outputWidth={1200}
                    title='Ajustar foto do produto'
                    description='Arraste para reposicionar e use o zoom. A foto será exibida em formato quadrado.'
                    onConfirm={handleCropConfirm}
                    onCancel={handleCropCancel}
                />
            </Field>

            <Field>
                <Label>Nome *</Label>
                <Input
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder='Ex: Smash Burger Clássico'
                    autoFocus
                />
            </Field>

            <Field>
                <Label>Descrição</Label>
                <TextArea
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    rows={2}
                    placeholder='Ingredientes, detalhes...'
                />
            </Field>

            <Field>
                <Label>Preço base *</Label>
                <PriceWrapper>
                    <CurrencyPrefix>R$</CurrencyPrefix>
                    <PriceInput
                        type='number'
                        step='0.01'
                        min='0'
                        value={basePrice}
                        onChange={e => setBasePrice(e.target.value)}
                        placeholder='0,00'
                    />
                </PriceWrapper>
            </Field>

            {!forcedType && (
                <Field>
                    <Label>Categoria *</Label>
                    <Select value={categoryId} onChange={e => setCategoryId(e.target.value)}>
                        <option value=''>Selecionar</option>
                        {categories.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </Select>
                </Field>
            )}

            <Field>
                <Label>Estação de pedido</Label>
                <Select value={orderStationId} onChange={e => setOrderStationId(e.target.value)}>
                    <option value=''>Nenhuma</option>
                    {stations.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                </Select>
                {!orderStationId && (
                    <FieldHint>Sem estação, o produto fica na comanda e não aparece em Pedidos.</FieldHint>
                )}
            </Field>

            <Field style={{ maxWidth: 120 }}>
                <Label>Ordem</Label>
                <Input
                    type='number'
                    min='0'
                    value={sortOrder}
                    onChange={e => setSortOrder(e.target.value)}
                    placeholder='Ex: 1'
                />
            </Field>

            {allGroups.length > 0 && (
                <Field>
                    <Label>Grupos de opções</Label>
                    <GroupList>
                        {allGroups.map(group => (
                            <GroupChip
                                key={group.id}
                                type='button'
                                $selected={selectedGroups.includes(group.id)}
                                onClick={() => toggleGroup(group.id)}
                            >
                                {group.name}
                            </GroupChip>
                        ))}
                    </GroupList>
                </Field>
            )}

            <Field>
                <Label>Ficha técnica (insumos)</Label>
                {techSheets.length > 0 && (
                    <TechSheetList>
                        {techSheets.map(t => {
                            const invItem = allInventoryItems.find(i => i.id === t.inventory_item_id);
                            return (
                                <TechSheetRow key={t.inventory_item_id}>
                                    <TechSheetInfo>
                                        {invItem
                                            ? `${invItem.name} — ${t.quantity_used} ${invItem.unit_of_measure}`
                                            : `Insumo removido — ${t.quantity_used}`}
                                    </TechSheetInfo>
                                    <RemoveRowBtn type='button' onClick={() => removeTechSheetRow(t.inventory_item_id)}>
                                        <X size={14} />
                                    </RemoveRowBtn>
                                </TechSheetRow>
                            );
                        })}
                    </TechSheetList>
                )}
                <TechSheetAddRow>
                    <ReactSelectWrapper>
                        <ReactSelect
                            placeholder='Selecionar insumo'
                            options={inventoryOptions}
                            value={selectedInventoryOption}
                            onChange={setSelectedInventoryOption}
                        />
                    </ReactSelectWrapper>
                    <Input
                        type='number'
                        step='0.0001'
                        min='0'
                        style={{ flex: 1 }}
                        placeholder={selectedInventoryOption
                            ? `Qtd. (${allInventoryItems.find(i => i.id === selectedInventoryOption.value)?.unit_of_measure || ''})`
                            : 'Qtd.'}
                        value={techQuantity}
                        onChange={e => setTechQuantity(e.target.value)}
                    />
                    <AddRowBtn type='button' onClick={addTechSheetRow}>Adicionar</AddRowBtn>
                </TechSheetAddRow>
                {techSheets.length > 0 && (
                    <CostHint $negative={marginValue < 0}>
                        Custo estimado: {formatCurrency(estimatedCost)}
                        {marginPct !== null && ` · Margem: ${formatCurrency(marginValue)} (${marginPct.toFixed(0)}%)`}
                    </CostHint>
                )}
            </Field>

            <VisibilitySection>
                <SectionLabel>Visibilidade</SectionLabel>
                <ToggleRow>
                    <ToggleInfo>
                        <ToggleName>Ativo</ToggleName>
                        <ToggleHint>Produto aparece no cardápio</ToggleHint>
                    </ToggleInfo>
                    <ToggleSwitch
                        $on={isActive}
                        onClick={() => setIsActive(v => !v)}
                        type="button"
                        role="switch"
                        aria-checked={isActive}
                    >
                        <ToggleKnob $on={isActive} />
                    </ToggleSwitch>
                </ToggleRow>
                <ToggleRow $disabled={!isActive}>
                    <ToggleInfo>
                        <ToggleName>Cardápio público</ToggleName>
                        <ToggleHint>Clientes podem ver este produto</ToggleHint>
                    </ToggleInfo>
                    <ToggleSwitch
                        $on={isPublic}
                        onClick={() => isActive && setIsPublic(v => !v)}
                        type="button"
                        role="switch"
                        aria-checked={isPublic}
                        disabled={!isActive}
                    >
                        <ToggleKnob $on={isPublic} />
                    </ToggleSwitch>
                </ToggleRow>
            </VisibilitySection>

            {error && <ErrorText>{error}</ErrorText>}

            <ButtonRow>
                {onCancel && (
                    <CancelBtn type='button' onClick={onCancel} disabled={loading}>
                        Cancelar
                    </CancelBtn>
                )}
                <SubmitBtn type='submit' disabled={loading}>
                    {loading ? 'Salvando...' : initialData ? 'Salvar alterações' : 'Criar produto'}
                </SubmitBtn>
            </ButtonRow>
        </Form>
    );
}

const Form = styled.form`display: flex; flex-direction: column; gap: 16px;`;
const ButtonRow = styled.div`display: flex; gap: 10px; justify-content: flex-end;`;

const ImageUploadArea = styled.div`
    width: 100%; height: 140px; border-radius: 12px; overflow: hidden; position: relative;
    border: 1.5px dashed ${({ $hasImage }) => $hasImage ? 'transparent' : colors.neutralDark};
    background: ${({ $hasImage }) => $hasImage ? 'transparent' : colors.neutralBackground || '#f9f9f9'};
    cursor: pointer; transition: border-color 0.15s;
    &:hover { border-color: ${panelAccent(colors.occasionalPurple)}; }
`;
const ImagePreview = styled.img`width: 100%; height: 100%; object-fit: cover;`;
const RemoveImageBtn = styled.button`
    position: absolute; top: 8px; right: 8px;
    width: 26px; height: 26px; border-radius: 50%;
    background: rgba(0,0,0,0.55); color: #fff; border: none;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
`;
const CropImageBtn = styled.button`
    position: absolute; top: 8px; right: 42px;
    width: 26px; height: 26px; border-radius: 50%;
    background: rgba(0,0,0,0.55); color: #fff; border: none;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
`;
const UploadPlaceholder = styled.div`
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    height: 100%; gap: 8px;
`;
const UploadHint = styled.span`font-size: 13px; color: #999;`;
const ImageHint = styled.span`font-size: 12px; color: ${colors.nightLight};`;
const FieldHint = styled.span`font-size: 12px; color: ${colors.nightLight};`;
const ImageErrorText = styled.span`font-size: 12px; color: ${colors.danger}; font-weight: 500;`;
const HiddenInput = styled.input`display: none;`;
const Field = styled.div`display: flex; flex-direction: column; gap: 6px;`;
const Label = styled.label`font-size: 13px; font-weight: 600; color: ${colors.nightDark};`;
const Input = styled.input`
    border: 1.5px solid ${colors.neutralDark}; border-radius: 10px; padding: 12px 14px;
    font-size: 15px; outline: none; color: ${colors.nightDark}; background: #fff;
    transition: border-color 0.15s;
    &:focus { border-color: ${panelAccent(colors.occasionalPurple)}; }
    &::placeholder { color: #999; }
`;
const TextArea = styled.textarea`
    border: 1.5px solid ${colors.neutralDark}; border-radius: 10px; padding: 12px 14px;
    font-size: 15px; outline: none; color: ${colors.nightDark}; resize: vertical; background: #fff;
    transition: border-color 0.15s;
    &:focus { border-color: ${panelAccent(colors.occasionalPurple)}; }
`;
const Row = styled.div`display: flex; gap: 12px;`;
const PriceWrapper = styled.div`
    display: flex; align-items: center;
    border: 1.5px solid ${colors.neutralDark}; border-radius: 10px; background: #fff;
    overflow: hidden; transition: border-color 0.15s;
    &:focus-within { border-color: ${panelAccent(colors.occasionalPurple)}; }
`;
const CurrencyPrefix = styled.span`
    padding: 12px 10px 12px 14px; font-size: 15px; color: #666;
    font-weight: 600; border-right: 1px solid ${colors.neutralDark}; background: ${colors.neutralDark};
`;
const PriceInput = styled.input`
    flex: 1; border: none; padding: 12px 14px; font-size: 15px; outline: none;
    color: ${colors.nightDark}; background: transparent;
`;
const Select = styled.select`
    border: 1.5px solid ${colors.neutralDark}; border-radius: 10px; padding: 12px 14px;
    font-size: 15px; background: #fff; outline: none; color: ${colors.nightDark};
    transition: border-color 0.15s;
    &:focus { border-color: ${panelAccent(colors.occasionalPurple)}; }
`;
const GroupList = styled.div`display: flex; flex-wrap: wrap; gap: 8px; margin-top: 4px;`;
const GroupChip = styled.button`
    padding: 7px 14px; border-radius: 20px; font-size: 13px; cursor: pointer; font-weight: 500;
    border: 1.5px solid ${({ $selected }) => $selected ? panelAccent(colors.occasionalPurple) : colors.neutralDark};
    background: ${({ $selected }) => $selected ? `${panelAccentAlpha(colors.occasionalPurple, '15')}` : 'transparent'};
    color: ${({ $selected }) => $selected ? panelAccent(colors.occasionalPurple) : colors.night};
    transition: all 0.15s;
`;
const VisibilitySection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 10px;
    overflow: hidden;
`;
const SectionLabel = styled.div`
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${colors.nightLight};
    padding: 10px 14px 6px;
    background: ${colors.neutralBackground || '#f9f9f9'};
    border-bottom: 1px solid ${colors.neutralDark};
`;
const ToggleRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 14px;
    gap: 12px;
    opacity: ${({ $disabled }) => $disabled ? 0.45 : 1};
    transition: opacity 0.15s;
    & + & { border-top: 1px solid ${colors.neutralDark}; }
`;
const ToggleInfo = styled.div`display: flex; flex-direction: column; gap: 2px;`;
const ToggleName = styled.span`font-size: 14px; font-weight: 600; color: ${colors.nightDark};`;
const ToggleHint = styled.span`font-size: 12px; color: ${colors.nightLight};`;
const ToggleSwitch = styled.button`
    width: 42px;
    height: 24px;
    border-radius: 12px;
    border: none;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    background: ${({ $on }) => $on ? colors.occasionalGreen : colors.neutralDark};
    position: relative;
    flex-shrink: 0;
    transition: background 0.2s;
    padding: 0;
`;
const ToggleKnob = styled.div`
    position: absolute;
    top: 3px;
    left: ${({ $on }) => $on ? '21px' : '3px'};
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
    transition: left 0.2s;
`;
const TechSheetList = styled.div`display: flex; flex-direction: column; gap: 6px; margin-bottom: 10px;`;
const TechSheetRow = styled.div`
    display: flex; align-items: center; justify-content: space-between; gap: 10px;
    padding: 10px 12px; border-radius: 10px; background: ${colors.neutralBackground || '#f9f9f9'};
    border: 1px solid ${colors.neutralDark};
`;
const TechSheetInfo = styled.span`font-size: 13px; font-weight: 500; color: ${colors.nightDark};`;
const RemoveRowBtn = styled.button`
    width: 22px; height: 22px; border-radius: 50%; flex-shrink: 0;
    background: rgba(0,0,0,0.08); color: ${colors.night}; border: none;
    display: flex; align-items: center; justify-content: center; cursor: pointer;
    &:hover { background: ${colors.danger}20; color: ${colors.danger}; }
`;
const TechSheetAddRow = styled.div`display: flex; gap: 8px; align-items: center;`;
const ReactSelectWrapper = styled.div`flex: 2;`;
const AddRowBtn = styled.button`
    flex-shrink: 0; padding: 12px 16px; border-radius: 10px; border: none;
    background: ${panelAccent(colors.occasionalPurple)}; color: #fff; font-size: 14px; font-weight: 600;
    cursor: pointer; transition: opacity 0.15s;
    &:hover { opacity: 0.9; }
`;
const CostHint = styled.p`
    font-size: 13px; font-weight: 600; margin: 8px 0 0;
    color: ${({ $negative }) => $negative ? colors.danger : colors.occasionalGreen};
`;
const ErrorText = styled.p`color: ${colors.danger}; font-size: 13px; margin: 0;`;
const SubmitBtn = styled.button`
    padding: 14px; border-radius: 10px;
    background: ${({ disabled }) => disabled ? colors.neutralDark : panelAccent(colors.occasionalPurple)};
    color: ${({ disabled }) => disabled ? colors.nightLight : '#fff'};
    font-size: 15px; font-weight: 600; border: none;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    &:hover:not(:disabled) { opacity: 0.9; }
`;
const CancelBtn = styled.button`
    padding: 14px 20px; border-radius: 10px;
    background: none; border: 1.5px solid ${colors.neutralDark};
    color: ${colors.nightDark}; font-size: 15px; font-weight: 600;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    transition: background 0.15s;
    &:hover:not(:disabled) { background: ${colors.neutralDark}; }
`;
